// API Configuration and Utilities
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.hypr.qa';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  count?: number;
}

export class ApiError extends Error {
  constructor(
    public message: string,
    public status?: number,
    /** Full response body — carries fields like blocked_orders that the
        message alone flattens away. */
    public payload?: any,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      credentials: 'include', // Send httpOnly session cookie with every request
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    const data: ApiResponse<T> = await response.json();

    if (!data.success) {
      throw new ApiError(
        data.message || 'API request failed',
        response.status,
        data,
      );
    }

    return data.data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      'Network error: Unable to connect to server',
      0
    );
  }
};

// Driver-specific API functions
export const driversApi = {
  getAll: () => apiRequest<any[]>('/api/drivers'),

  create: (name: string, phone: string) =>
    apiRequest('/api/drivers', {
      method: 'POST',
      body: JSON.stringify({ name, phone }),
    }),

  assign_driver: (driver_id: string, order_number: string) =>
    apiRequest('/api/drivers/assign', {
      method: 'POST',
      body: JSON.stringify({ "driver_id": driver_id, "order_number": order_number }),
    }),

  delete: (phone: string) =>
    apiRequest(`/api/drivers/${encodeURIComponent(phone)}`, {
      method: 'DELETE',
    }),
};

// Analytics-specific API functions
export interface Analytics {
  total_orders: number;
  active_orders: number;
  delivered_orders: number;
  orders_missing_location: number;
}

export const analyticsApi = {
  // Lives under the orders router; /api/analytics has never existed.
  get: () => apiRequest<Analytics>('/api/orders/analytics'),
};

// Orders-specific API functions
export const ordersApi = {
  getAll: () => apiRequest<any[]>('/api/orders'),

  getAllDriverOrders: (driverId: string) =>
    apiRequest<any[]>(`/api/orders/driver/${encodeURIComponent(driverId)}`),

  getLocations: () =>
    apiRequest<any[]>('/api/orders/locations'),

  assign: (orderIds: string[], driverId: string) =>
    apiRequest('/api/orders/assign', {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds, driver_id: driverId }),
    }),
};

// Route optimization API functions
export const routeApi = {
  optimize: (payload: any) =>
    apiRequest<{ route: any[]; maps_url: string }>('/api/route/optimize', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  optimiseByOrderNumber: (payload: any) =>
    apiRequest<{ route: any[]; maps_url: string }>('/api/route/byOrderNumber', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** Which of these orders can be routed, without running the optimizer.
      Catches what the client cannot see on its own — a shop with no
      coordinates on file. */
  validate: (order_ids: string[]) =>
    apiRequest<{
      can_optimize: boolean;
      ready_order_numbers: string[];
      blocked_orders: { order_number: string; customer_name: string; reasons: string[] }[];
    }>('/api/route/validate', {
      method: 'POST',
      body: JSON.stringify({ order_ids }),
    }),
};

export const shopAPI = {
  getAll: () => apiRequest<any[]>('/api/shops/all'),

  create: ({
    name,
    latitude,
    longitude,
    phone,
    product_type,
    cr_number,
  }: {
    name: string;
    latitude: number;
    longitude: number;
    phone: string;
    product_type: string;
    cr_number: string;
  }) =>
    apiRequest('/api/shops/create', {
      method: 'POST',
      body: JSON.stringify({
        name,
        latitude,
        longitude,
        phone,
        product_type,
        cr_number,
      }),
    }),

  onboardByPhone: async (payload: { phone: string }) => {
    const res = await fetch(`${API_BASE_URL}/api/shops/onboard`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};